/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import PropTypes from 'prop-types';
import FocusLock from 'react-focus-lock';

import {AuthHelpers, useAuthHelper, useCustomerType} from '@salesforce/commerce-sdk-react';
import {
  AccountIcon, BasketIcon, QuoteIcon, LocationDetailIcon,
} from '@salesforce/retail-react-app/app/components/icons';
import {BrandLogo, LogoutIcon, ProfileCircleIcon, SearchIcon} from '@amblique/king-living/app/components/icons';
import {useHeaderContext} from '@amblique/king-living/app/contexts/header-context';
import {useCartFlyoutContext} from '@amblique/king-living/app/contexts/cart-flyout-context';
import {HamburgerToCloseIcon} from '@amblique/king-living/app/components/icons/hamburger-to-close-icon';
import Link from '@salesforce/retail-react-app/app/components/link';
import {PopoverAnchor} from '@amblique/base/app/components/shared/ui/PopoverAnchor';
import {
  Badge, Box, Button, Divider, Flex, IconButton, Popover,
  PopoverArrow, PopoverBody, PopoverContent, Spinner, Text, VStack,
  useDisclosure, useMediaQuery, useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import withRegistration from '@salesforce/retail-react-app/app/components/with-registration';
import {STORE_LOCATOR_IS_ENABLED} from '@salesforce/retail-react-app/app/constants';
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket';
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer';
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import {isHydrated, noop} from '@salesforce/retail-react-app/app/utils/utils';
import {getCustomerInitials, getMyShowroomFromCookie, SHOWROOM_CHANGED_EVENT} from '@amblique/king-living/app/utils/utils';
import {groupComplexLineItems} from '@amblique/king-living/app/utils/cart-utils';
import {isHeaderWishlistIconEnabled} from '@amblique/king-living/app/constants/header';
import {SHOWROOMS_LISTING_PATH} from '@amblique/king-living/app/constants/site-config';
import {useEscapeToClose} from '@amblique/king-living/app/hooks/use-escape-to-close';
import LocaleSelector from '../locale-selector';

const IconButtonWithRegistration = withRegistration(IconButton);

/** Delay before closing the account menu after the pointer leaves the trigger (allows moving into portaled content). */
const ACCOUNT_MENU_TRIGGER_LEAVE_MS = 100;

/**
 * The header is the main source for accessing
 * navigation, search, basket, and other
 * important information and actions. It persists
 * on the top of your application and will
 * respond to changes in device size.
 *
 * To customize the styles, update the themes
 * in theme/components/project/header.js
 * @param  props
 * @param   {func} props.onMenuClick click event handler for menu button
 * @param   {func} props.onLogoClick click event handler for menu button
 * @param   {object} props.searchInputRef reference of the search input
 * @param   {func} props.onMyAccountClick click event handler for my account button
 * @param   {func} props.onMyCartClick click event handler for my cart button
 * @param   {boolean} props.isMenuOpen whether the mobile menu is open
 * @return  {React.ReactElement} - Header component
 */
const Header = ({
  children,
  onMenuClick = noop,
  onMyAccountClick = noop,
  onLogoClick = noop,
  onMyCartClick = noop,
  onWishlistClick = noop,
  onStoreLocatorClick = noop,
  onSearchBoxClick = noop,
  onSearchBoxClose = noop,
  isSearchBoxOpen = false,
  isMenuOpen = false,
  ...props
}) => {
  const intl = useIntl();
  const popoverTriggerRef = useRef(null);
  const hasEnterPopoverContent = useRef(false);
  const accountMenuOpenedByHoverRef = useRef(false);
  const accountMenuFirstFocusableRef = useRef(null);
  const pendingKeyboardFocusIntoAccountMenuRef = useRef(false);
  const [accountMenuKeyboardMode, setAccountMenuKeyboardMode] = useState(false);
  const {data: basket} = useCurrentBasket();

  const totalItems = useMemo(() => {
    const {visibleItems} = groupComplexLineItems(basket?.productItems);
    return visibleItems.reduce((acc, item) => acc + item.quantity, 0);
  }, [basket?.productItems]);
  const {isRegistered} = useCustomerType();
  const {data: customer, isError: isCustomerError, isFetched: isCustomerFetched, isLoading: isCustomerLoading} = useCurrentCustomer();
  const logout = useAuthHelper(AuthHelpers.Logout);
  const navigate = useNavigation();
  const {site, buildUrl} = useMultiSite();
  const showWishlistIcon = isHeaderWishlistIconEnabled();
  const {
    getDisclosureProps: getAccountMenuDisclosureProps,
    isOpen: isAccountMenuOpen,
    onClose: onAccountMenuClose,
    onOpen: onAccountMenuOpen,
  } = useDisclosure();

  const closeAccountMenu = useCallback(() => {
    accountMenuOpenedByHoverRef.current = false;
    pendingKeyboardFocusIntoAccountMenuRef.current = false;
    setAccountMenuKeyboardMode(false);
    onAccountMenuClose();
  }, [onAccountMenuClose]);
  const {onOpen: openCartFlyout} = useCartFlyoutContext();
  const [isDesktop] = useMediaQuery('(min-width: 992px)');
  const [isSigningOut, setIsSigningOut] = useState(false);

  // State for user's preferred showroom
  const [myShowroom, setMyShowroom] = useState(null);

  // Load showroom from cookie on mount and listen for changes
  useEffect(() => {
    if (typeof window === 'undefined' || !site?.id) return;

    // Load initial value from cookie
    const savedShowroom = getMyShowroomFromCookie(site.id);

    if (savedShowroom) {
      setMyShowroom(savedShowroom);
    }

    // Listen for showroom changes from other components
    const handleShowroomChanged = (event) => {
      setMyShowroom(event.detail?.showroom || null);
    };

    window.addEventListener(SHOWROOM_CHANGED_EVENT, handleShowroomChanged);

    return () => {
      window.removeEventListener(SHOWROOM_CHANGED_EVENT, handleShowroomChanged);
    };
  }, [site?.id]);

  const focusAccountMenuFirstItem = useCallback(() => {
    pendingKeyboardFocusIntoAccountMenuRef.current = true;
    setAccountMenuKeyboardMode(true);
  }, []);

  useEffect(() => {
    if (!isAccountMenuOpen || !pendingKeyboardFocusIntoAccountMenuRef.current) return;

    let cancelled = false;
    let attempts = 0;

    const tryFocus = () => {
      if (cancelled) return;

      const firstItem = accountMenuFirstFocusableRef.current;
      if (firstItem) {
        pendingKeyboardFocusIntoAccountMenuRef.current = false;
        firstItem.focus({preventScroll: true});

        return;
      }

      if (attempts++ < 20) {
        requestAnimationFrame(tryFocus);
      } else {
        pendingKeyboardFocusIntoAccountMenuRef.current = false;
      }
    };

    tryFocus();

    return () => {
      cancelled = true;
    };
  }, [isAccountMenuOpen, accountMenuKeyboardMode]);

  const handleAccountMenuEscapeClose = useCallback(() => {
    closeAccountMenu();
    requestAnimationFrame(() => popoverTriggerRef.current?.focus({preventScroll: true}));
  }, [closeAccountMenu]);

  useEscapeToClose(isAccountMenuOpen, handleAccountMenuEscapeClose);

  // Get header state from context for dynamic styling
  const {headerState, headerStyle, isSticky, isSearchOpen, setHeaderHovered} = useHeaderContext();

  // Pass header state and variant to theme for dynamic styles
  // variant: 'light' or 'dark' determines icon/text colors
  const styles = useMultiStyleConfig('Header', {
    headerState,
    isSticky,
    variant: headerStyle, // Use headerStyle as variant for color scheme
  });

  // Handle header hover for transparent/gradient states
  const handleHeaderMouseEnter = () => setHeaderHovered(true);
  const handleHeaderMouseLeave = () => setHeaderHovered(false);

  const handleAccountTriggerMouseLeave = useCallback(() => {
    setTimeout(() => {
      if (!hasEnterPopoverContent.current) closeAccountMenu();
    }, ACCOUNT_MENU_TRIGGER_LEAVE_MS);
  }, [closeAccountMenu]);

  const handleAccountPopoverContentMouseOver = useCallback(() => {
    hasEnterPopoverContent.current = true;
  }, []);

  const handleAccountPopoverContentMouseLeave = useCallback(() => {
    hasEnterPopoverContent.current = false;
    closeAccountMenu();
  }, [closeAccountMenu]);

  const handleAccountTriggerMouseEnter = useCallback(() => {
    if (!isDesktop || isAccountMenuOpen || isSearchOpen) return;
    accountMenuOpenedByHoverRef.current = true;
    onAccountMenuOpen();
  }, [isAccountMenuOpen, isDesktop, isSearchOpen, onAccountMenuOpen]);

  const handleAccountTriggerClick = useCallback(() => {
    if (!isDesktop) return;
    if (isSearchOpen) {
      onSearchBoxClose();
    }
    if (!isAccountMenuOpen) {
      accountMenuOpenedByHoverRef.current = false;
      onAccountMenuOpen();

      return;
    }
    if (accountMenuOpenedByHoverRef.current) {
      accountMenuOpenedByHoverRef.current = false;

      return;
    }
    closeAccountMenu();
  }, [closeAccountMenu, isAccountMenuOpen, isDesktop, isSearchOpen, onAccountMenuOpen, onSearchBoxClose]);

  const handleAccountTriggerFocus = useCallback(() => {
    if (isAccountMenuOpen) {
      setAccountMenuKeyboardMode(true);
    }
  }, [isAccountMenuOpen]);

  const handleAccountMenuKeyDown = useCallback(
    (event) => {
      if (event.key === 'Tab' && isAccountMenuOpen && !event.shiftKey) {
        event.preventDefault();
        focusAccountMenuFirstItem();

        return;
      }

      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();

      if (!isAccountMenuOpen) {
        if (isSearchOpen) {
          onSearchBoxClose();
        }
        accountMenuOpenedByHoverRef.current = false;
        focusAccountMenuFirstItem();
        onAccountMenuOpen();

        return;
      }

      if (accountMenuOpenedByHoverRef.current) {
        accountMenuOpenedByHoverRef.current = false;
        focusAccountMenuFirstItem();

        return;
      }

      closeAccountMenu();
    },
    [
      closeAccountMenu,
      focusAccountMenuFirstItem,
      isAccountMenuOpen,
      isSearchOpen,
      onAccountMenuOpen,
      onSearchBoxClose,
    ]
  );

  const onSignoutClick = async () => {
    if (isSigningOut) return;
    setIsSigningOut(true);
    closeAccountMenu();
    try {
      await logout.mutateAsync();
      navigate(buildUrl('/'), 'replace');
    } finally {
      setIsSigningOut(false);
    }
  };

  const accountDisplayName = [customer?.firstName, customer?.lastName].filter(Boolean).join(' ').trim();
  const firstCharsName = getCustomerInitials(customer);
  const isAccountAvatarLoading = isRegistered &&
    !firstCharsName &&
    !isCustomerError &&
    (isCustomerLoading || !isCustomerFetched);
  const isSearchNavHidden = isDesktop && (isSearchOpen || isSearchBoxOpen);

  return (
    <Box
      className="header-wrapper"
      onMouseEnter={handleHeaderMouseEnter}
      onMouseLeave={handleHeaderMouseLeave}
      {...styles.container}
      {...props}
    >
      <Box className="header-content-wrapper" {...styles.content}>
        <Flex justifyContent="space-between" wrap="wrap">
          <IconButton
            className="header-logo-button"
            icon={<BrandLogo className="header-logo" {...styles.logo} />}
            aria-label={intl.formatMessage({
              id: 'header.button.assistive_msg.logo',
              defaultMessage: 'Logo',
            })}
            {...styles.iconLogo}
            variant="unstyled"
            onClick={onLogoClick}
          />
          <Box {...styles.actionsContainer}>
            {/* Desktop: Show store icon + text */}
            {STORE_LOCATOR_IS_ENABLED && (
              <Button
                data-showroom-trigger
                variant="unstyled"
                aria-label={myShowroom ?
                  intl.formatMessage({
                    defaultMessage: 'My Showroom: {name}',
                    id: 'header.button.assistive_msg.my_showroom',
                  }, {name: myShowroom.name}) :
                  intl.formatMessage({
                    defaultMessage: 'Set your showroom',
                    id: 'header.button.assistive_msg.set_showroom',
                  })}
                onClick={() => {
                  if (isSearchOpen) {
                    onSearchBoxClose();
                  }
                  if (myShowroom?.slug) {
                    navigate(buildUrl(`/showroom/${myShowroom.slug}`));
                  } else {
                    navigate(buildUrl(SHOWROOMS_LISTING_PATH));
                  }
                }}
                {...styles.buttonLocationIcon}
              >
                <LocationDetailIcon data-showroom-trigger-icon {...styles.icons} />
                <Flex align="center" display={{base: 'none', lg: 'inline-block'}} gap={2}>
                  <Text
                    as="span"
                    data-showroom-trigger-label
                    {...styles.buttonLocationText}
                  >
                    {(myShowroom?.name ?
                      myShowroom.name :
                      intl.formatMessage({
                        defaultMessage: 'Set your showroom',
                        id: 'header.button.set_showroom',
                      }))}
                  </Text>
                </Flex>
              </Button>
            )}

            <IconButton
              data-header-search-trigger
              icon={<SearchIcon {...styles.searchIcon} />}
              {...styles.icons}
              {...styles.searchIconButton}
              variant="unstyled"
              aria-label={intl.formatMessage({
                defaultMessage: 'Search',
                id: 'header.button.assistive_msg.search',
              })}
              onClick={onSearchBoxClick}
            />
            <Box {...styles.languageIconDesktop}>
              <LocaleSelector variant="desktop" />
            </Box>
            <Box
              alignItems="center"
              className="account-menu-container"
              display={{base: 'none', lg: 'inline-flex'}}
            >
              {isRegistered && isHydrated() ? (
                <Popover
                  {...styles.accountMenuPopover}
                  arrowSize={15}
                  className="account-menu-popover"
                  isOpen={isAccountMenuOpen}
                  closeOnBlur={false}
                  placement="bottom"
                  isLazy
                  returnFocusOnClose
                  onClose={closeAccountMenu}
                >
                  <PopoverAnchor>
                    <Box
                      ref={popoverTriggerRef}
                      aria-expanded={isAccountMenuOpen}
                      aria-haspopup="true"
                      className="account-name-badge"
                      role="button"
                      {...styles.accountNameBadge}
                      {...styles.mobileDrawerFooterIconButton}
                      tabIndex={0}
                      aria-label={intl.formatMessage({
                        id: 'header.button.assistive_msg.my_account_menu',
                        defaultMessage: 'My account',
                      })}
                      onClick={handleAccountTriggerClick}
                      onFocus={handleAccountTriggerFocus}
                      onKeyDown={handleAccountMenuKeyDown}
                      onMouseEnter={handleAccountTriggerMouseEnter}
                      onMouseLeave={handleAccountTriggerMouseLeave}
                    >
                      {isAccountAvatarLoading ? (
                        <Spinner aria-hidden color="currentColor" size="xs" thickness="1.5px" />
                      ) : (
                        firstCharsName
                      )}
                    </Box>
                  </PopoverAnchor>
                  <PopoverContent
                    className="account-menu-popover-content"
                    {...styles.popoverContent}
                    {...getAccountMenuDisclosureProps()}
                    onMouseLeave={handleAccountPopoverContentMouseLeave}
                    onMouseOver={handleAccountPopoverContentMouseOver}
                  >
                    <PopoverArrow />
                    <PopoverBody
                      className="account-menu-popover-content-body"
                      {...styles.accountPopoverContentBody}
                    >
                      <FocusLock disabled={!accountMenuKeyboardMode} returnFocus>
                        <Box className="account-menu-popover-content__user-block" {...styles.accountPopoverUserBlock}>
                        <VStack align="stretch" spacing={1}>
                          <Text className="account-menu-popover-content__account-name" {...styles.accountPopoverText}>
                            {accountDisplayName || ''}
                          </Text>
                          {Boolean(customer?.email && accountDisplayName) && (
                            <Text
                              color="gray.600"
                              fontSize="sm"
                              noOfLines={2}
                              {...styles.accountPopoverEmailText}
                            >
                              {customer.email}
                            </Text>
                          )}
                        </VStack>
                      </Box>
                      <Divider {...styles.accountPopoverDivider} />
                      <Button
                        ref={accountMenuFirstFocusableRef}
                        as={Link}
                        className="account-menu-popover-content__account-link"
                        {...styles.accountPopoverAccountLink}
                        to="/account"
                        useNavLink
                        onClick={closeAccountMenu}
                      >
                        <ProfileCircleIcon boxSize="20px" aria-hidden {...styles.accountPopoverAccountIcon} />
                        <Text as="span" {...styles.accountPopoverText}>
                          {intl.formatMessage({
                            defaultMessage: 'My account',
                            id: 'header.popover.title.my_account',
                          })}
                        </Text>
                      </Button>
                      <Divider {...styles.accountPopoverDivider} />
                      <Button
                        className="account-menu-popover-content__signout-link"
                        type="button"
                        variant="unstyled"
                        {...styles.accountPopoverAccountLink}
                        width="100%"
                        onClick={onSignoutClick}
                      >
                        <LogoutIcon boxSize="20px" aria-hidden {...styles.accountPopoverAccountIcon} />
                        <Text as="span" {...styles.accountPopoverText}>
                          {intl.formatMessage({
                            defaultMessage: 'Sign out',
                            id: 'header.popover.action.sign_out',
                          })}
                        </Text>
                      </Button>
                      </FocusLock>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              ) : (
                <IconButton
                  icon={<AccountIcon />}
                  variant="unstyled"
                  aria-label={intl.formatMessage({
                    id: 'header.button.assistive_msg.my_account',
                    defaultMessage: 'My Account',
                  })}
                  {...styles.icons}
                  {...styles.accountIcon}
                  onClick={onMyAccountClick}
                />
              )}
            </Box>

            {showWishlistIcon && (
              <IconButtonWithRegistration
                icon={<QuoteIcon />}
                variant="unstyled"
                aria-label={intl.formatMessage({
                  defaultMessage: 'Wishlist',
                  id: 'header.button.assistive_msg.wishlist',
                })}
                {...styles.icons}
                {...styles.wishlistIconDesktop}
                onClick={onWishlistClick}
              />
            )}
            <IconButton
              variant="unstyled"
              aria-label={intl.formatMessage(
                {
                  id: 'header.button.assistive_msg.my_cart_with_num_items',
                  defaultMessage: 'My cart, number of items: {numItems}',
                },
                {numItems: totalItems}
              )}
              icon={
                <>
                  <BasketIcon />
                  {basket && totalItems > 0 && (
                    <Badge {...styles.cartBadge}>{totalItems}</Badge>
                  )}
                </>
              }
              {...styles.icons}
              {...styles.cartIcon}
              onClick={() => {
                if (isSearchOpen) {
                  onSearchBoxClose();
                }
                openCartFlyout();
                onMyCartClick();
              }}
            />

            <IconButton
              display={{lg: 'none'}}
              icon={<HamburgerToCloseIcon isOpen={isMenuOpen} />}
              variant="unstyled"
              aria-label={intl.formatMessage({
                id: 'header.button.assistive_msg.menu',
                defaultMessage: 'Menu',
              })}
              title={intl.formatMessage({
                id: 'header.button.assistive_msg.menu.open_dialog',
                defaultMessage: 'Opens a dialog',
              })}
              {...styles.icons}
              {...styles.iconHamburger}
              onClick={onMenuClick}
            />
          </Box>

        </Flex>

        <Box
          {...styles.bodyContainer}
          aria-hidden={isSearchNavHidden ? 'true' : undefined}
          data-search-nav-hidden={isSearchNavHidden ? 'true' : undefined}
          display={isSearchNavHidden ? {base: 'block', lg: 'none'} : undefined}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

Header.propTypes = {
  children: PropTypes.node,
  onMenuClick: PropTypes.func,
  onLogoClick: PropTypes.func,
  onMyAccountClick: PropTypes.func,
  onWishlistClick: PropTypes.func,
  onMyCartClick: PropTypes.func,
  onStoreLocatorClick: PropTypes.func,
  onSearchBoxClick: PropTypes.func,
  onSearchBoxClose: PropTypes.func,
  isSearchBoxOpen: PropTypes.bool,
  isMenuOpen: PropTypes.bool,
  searchInputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({current: PropTypes.elementType})
  ]),
};

export default Header;
